import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@common/components/button';
import { useTranslation } from 'react-i18next';
import TextAreaInput from '@common/components/form/inputs/textarea/textarea-input';

interface AnswerCommentBoxProps {
  userFullName: string;
  answer: string;
  isReadOnly: boolean;
  canEdit: boolean;
  onEdit?: () => void;
  onSave?: (newAnswer: string) => void;
  className?: string;
  showAsNotSubmitted?: boolean;
  enableInlineEdit?: boolean;
}

const AnswerCommentBox: React.FC<AnswerCommentBoxProps> = ({
  userFullName,
  answer,
  isReadOnly,
  canEdit,
  onEdit,
  onSave,
  className = '',
  showAsNotSubmitted = false,
  enableInlineEdit = false,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(answer || '');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Update edit value when answer prop changes
  useEffect(() => {
    setEditValue(answer || '');
  }, [answer]);

  const handleSave = () => {
    if (onSave) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(answer || '');
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (enableInlineEdit) {
      setIsEditing(true);
      setTimeout(() => {
        const inputElement = inputRef.current;
        if (inputElement) {
          inputElement.focus();
          // Move the cursor to the end
          const { length } = inputElement.value;
          inputElement.setSelectionRange(length, length);
        }
      }, 100);
    } else if (onEdit) {
      onEdit();
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-text-gray-textDark tw-text-base tw-font-semibold">
        {userFullName}
      </div>

      {isEditing && enableInlineEdit ? (
        <div className="tw-flex tw-flex-col tw-gap-3 !tw-cursor-text">
          <div className={`tw-bg-gray-custom-50 tw-border-[2px]  !tw-rounded-lg ${className}`}>
            <TextAreaInput
              ref={inputRef}
              value={editValue}
              onChange={setEditValue}
              placeholder={t('performance:write_your_answer')}
              minRows={3}
              className="!tw-bg-white !tw-border-gray-border"
            />
          </div>
          <div className="tw-flex tw-flex-row tw-gap-2 tw-justify-end">
            <Button
              type="default"
              size="small"
              onClick={handleCancel}
            >
              {t('performance:cancel')}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleSave}
            >
              {t('performance:save')}
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`tw-flex tw-flex-row tw-items-center tw-justify-between tw-p-4
            tw-bg-gray-custom-50 tw-border-[2px] tw-border-dashed
            tw-border-gray-disabled tw-borderImage !tw-rounded-lg tw-text-paragraph !tw-cursor-text
            tw-text-base tw-font-medium ${className}`}
          {...(canEdit && {
              onClick: () => {
                if (isReadOnly) return;
                handleEdit();
              }
            })}
        >
          <div
            className={`tw-text-base tw-font-medium ${
              showAsNotSubmitted || !answer ? 'tw-text-gray-disabled' : 'tw-text-paragraph'
            }`}
          >
            {showAsNotSubmitted ? t('performance:not_submitted') : (answer || t('performance:no_answer'))}
          </div>
          {canEdit && (
            <Button
              type="transparent"
              icon="edit"
              size="small"
              className="!tw-p-0"
              disabled={isReadOnly}
              onClick={() => {
                if (isReadOnly) return;
                handleEdit();
              }}
            >
              {t('performance:edit')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AnswerCommentBox;
